"use client";

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings, interpolate } from "@/i18n/dictionaries";

type PaidPlanId = "basic" | "standard" | "pro" | "interview-copilot-only";
type BillingDuration = "monthly" | "quarterly" | "semiannual";
type PaymentGateway = "stripe" | "flutterwave";

interface ConfirmBeforePaymentModalProps {
  customerName?: string | null;
  planId: PaidPlanId;
  planName: string;
  duration: BillingDuration;
  price: number;
  gateway: PaymentGateway;
  interviewHoursPerMonth?: number;
  emailAutoApplyCountryCount?: number;
  onConfirm: () => void;
  onClose: () => void;
}

const PLAN_CONFIRMATION_KEYS: Record<PaidPlanId, string[]> = {
  basic: [
    "confirm_shared_outcome",
    "confirm_basic_extension",
    "confirm_basic_benefits",
    "confirm_shared_ai_rules",
  ],
  standard: [
    "confirm_shared_outcome",
    "confirm_standard_extension",
    "confirm_standard_email",
    "confirm_standard_allowances",
  ],
  pro: [
    "confirm_shared_outcome",
    "confirm_pro_extension",
    "confirm_pro_email",
    "confirm_pro_allowances",
  ],
  "interview-copilot-only": [
    "confirm_copilot_outcome",
    "confirm_copilot_features",
    "confirm_copilot_exclusions",
    "confirm_copilot_allowance",
  ],
};

function firstNameFrom(value?: string | null) {
  return String(value || "").trim().split(/\s+/)[0] || "";
}

export default function ConfirmBeforePaymentModal({
  customerName,
  planId,
  planName,
  duration,
  price,
  gateway,
  interviewHoursPerMonth = 1,
  emailAutoApplyCountryCount = 49,
  onConfirm,
  onClose,
}: ConfirmBeforePaymentModalProps) {
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "pricing"), [locale]);
  const tc = useMemo(() => getStrings(locale, "copilot_product"), [locale]);
  const customerFirstName = firstNameFrom(customerName);

  const priceText = useMemo(
    () =>
      new Intl.NumberFormat(locale || "en", {
        style: "currency",
        currency: "USD",
        currencyDisplay: "code",
      }).format(Number.isFinite(price) ? price : 0),
    [locale, price]
  );

  const variables = useMemo(
    () => ({
      name: customerFirstName,
      plan: planName,
      duration: t[duration] || duration,
      price: priceText,
      term: t[`confirm_term_${duration}`] || t[duration] || duration,
      liveMinutes:
        planId === "basic" ? 60 : planId === "standard" ? 150 : 300,
      mockMinutes:
        planId === "basic" ? 90 : planId === "standard" ? 225 : 450,
      hours: interviewHoursPerMonth,
      countryCount: emailAutoApplyCountryCount,
      provider:
        gateway === "stripe"
          ? t.confirm_provider_stripe
          : t.confirm_provider_flutterwave,
    }),
    [
      customerFirstName,
      duration,
      emailAutoApplyCountryCount,
      gateway,
      interviewHoursPerMonth,
      planId,
      planName,
      priceText,
      t,
    ]
  );

  const confirmations = useMemo(() => {
    const planKeys = PLAN_CONFIRMATION_KEYS[planId];
    const billingKey =
      gateway === "stripe"
        ? "confirm_billing_stripe"
        : "confirm_billing_flutterwave";
    return [...planKeys, billingKey].map((key) => interpolate(t[key], variables));
  }, [gateway, planId, t, variables]);

  const title = interpolate(
    customerFirstName
      ? t.confirm_purchase_title_named
      : t.confirm_purchase_title,
    variables
  );
  const summary = interpolate(t.confirm_purchase_summary, variables);
  const [checked, setChecked] = useState<boolean[]>(
    Array(confirmations.length).fill(false)
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setChecked(Array(confirmations.length).fill(false));
  }, [confirmations.length, gateway, planId]);

  const allChecked = checked.every(Boolean);

  const toggle = (index: number) => {
    setChecked((previous) =>
      previous.map((value, currentIndex) =>
        currentIndex === index ? !value : value
      )
    );
  };

  const acceptAll = () => {
    setChecked(Array(confirmations.length).fill(true));
    window.setTimeout(onConfirm, 200);
  };

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-confirmation-title"
        className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl sm:p-6"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-400 transition hover:text-gray-600"
          aria-label={tc.close}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <div className="mb-3 rounded-xl border border-violet-100 bg-violet-50 p-3 pr-8 text-center">
          <h2
            id="purchase-confirmation-title"
            className="text-base font-bold text-violet-800 sm:text-lg"
          >
            {title}
          </h2>
          <p className="mt-1 text-xs font-semibold text-violet-600 sm:text-sm">
            {summary}
          </p>
        </div>
        <p className="mb-4 text-center text-xs text-gray-500 sm:text-sm">
          {t.confirm_subtitle_compact}
        </p>

        <div className="space-y-3">
          {confirmations.map((text, index) => (
            <label
              key={`${planId}-${gateway}-${index}`}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-100 p-3 transition hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={checked[index]}
                onChange={() => toggle(index)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-xs leading-relaxed text-gray-700 sm:text-sm">
                {text}
              </span>
            </label>
          ))}
        </div>

        <div className="my-4 h-px bg-gray-200" />

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={acceptAll}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 sm:w-auto"
          >
            {t.confirm_btn_accept_all}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!allChecked}
            className="w-full rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-purple-300 sm:w-auto"
          >
            {t.confirm_btn_continue}
          </button>
        </div>

        <p className="mt-3 text-center text-xs text-gray-400">
          {t.confirm_footer_compact}
        </p>
      </div>
    </div>,
    document.body
  );
}
